import { ForwardedRef, forwardRef } from 'react';
import TextField from '@mui/material/TextField';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  Box,
  createFilterOptions,
  styled,
  SxProps,
  Typography,
  FormHelperText,
} from '@mui/material';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

const Label = styled('label')({
  textAlign: 'left',
  fontWeight: '500',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1px',
  display: 'block',
  color: 'var(--gray-700)',
});

export interface TOption {
  label: string;
  value: string | number;
}

export interface AutocompleteProps
  extends Omit<
    MuiAutocompleteProps<TOption, false, false, false>,
    'renderInput'
  > {
  title?: string;
  inputStyle?: SxProps;
  labelStyle?: SxProps;
  value?: TOption | any;
  errorMessage?: string;
  showErrorMsg?: boolean;
  onBlur?: any;
  placeholder?: string;
}

export const Autocomplete = forwardRef(
  (props: AutocompleteProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      title,
      options,
      value,
      inputStyle,
      labelStyle = {},
      errorMessage,
      onBlur,
      showErrorMsg = false,
      placeholder,
      ...otherProps
    } = props;

    const filterOptions = createFilterOptions<TOption>({
      matchFrom: 'start',
      stringify: (option: TOption) => option.label,
    });


    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{
          ...inputStyle,
        }}
      >
        {title && <Label sx={labelStyle}>{title}</Label>}
        <MuiAutocomplete<TOption, false, false, false>
          options={options}
          value={options.find((option) => option.value === value)}
          getOptionLabel={(option) => option.label}
          filterOptions={filterOptions}
          sx={{
            '.MuiFormControl-root': {
              '.MuiAutocomplete-inputRoot': {
                backgroundColor: 'var(--base-white)',
                boxShadow: 'var(--shadow-xsm)',
                height: '42px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: showErrorMsg ? 'red' : 'var(--neutral-700)',
                  boxShadow: 'var(--shadow-xsm)',
                },
                input: {
                  padding: '2px 4px 2px 8px !important',
                },
              },
            },
          }}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props as {
              key: React.Key;
            } & React.HTMLAttributes<HTMLLIElement>;
            return (
              <Box
                key={key}
                component="li"
                display="flex"
                alignItems="center"
                width="100%"
                {...otherProps}
              >
                <Typography
                  variant="caption"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  color="var(--gray-700)"
                  fontWeight={500}
                >
                  {option.label}
                </Typography>
              </Box>
            );
          }}
          popupIcon={
            <ExpandMoreRoundedIcon sx={{ color: 'var(--gray-700)' }} />
          }
          {...otherProps}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '18px',
                  letterSpacing: '-0.1px',
                  ...inputStyle,
                  px: 0,
                }}
                ref={ref}
              />
            );
          }}
        />
        {showErrorMsg && (
          <FormHelperText
            sx={{
              width: '100%',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '20px',
              letterSpacing: '-0.1px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'var(--red-600)',
            }}
          >
            <Typography component={'span'} color={'var(--danger-500)'} sx={{
              marginTop: -1
            }}>
              {errorMessage}
            </Typography>
          </FormHelperText>
        )}
      </Box>
    );
  },
);

export default Autocomplete;
